/**
 * Topbar clock chip + calendar popover.
 *
 * Extracted out of MainLayout: the 60s tick used to live as `now` state on
 * MainLayout itself, which re-rendered the entire shell (including nav rows
 * defined as inline component types) every minute — remounting every NavRow
 * and destroying focus on the pin stars. Isolating the tick here means only
 * this chip re-renders on the interval.
 */
import React from 'react';
import { Box, Typography, IconButton, Chip, Popover, useTheme } from '@mui/material';

export function ClockChip() {
  const theme = useTheme();
  const P = theme.palette;

  const [now, setNow] = React.useState(new Date());
  const [calendarAnchor, setCalendarAnchor] = React.useState<null | HTMLElement>(null);
  const [calViewDate, setCalViewDate] = React.useState(() => new Date());

  // Live clock — update every minute
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // ── Calendar computation ──
  const calYear = calViewDate.getFullYear();
  const calMonth = calViewDate.getMonth();
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const isCurrentMonth = calYear === now.getFullYear() && calMonth === now.getMonth();
  const todayDate = now.getDate();
  const calDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calDays.push(d);

  const calPrev = () => setCalViewDate(new Date(calYear, calMonth - 1, 1));
  const calNext = () => setCalViewDate(new Date(calYear, calMonth + 1, 1));
  const calToday = () => setCalViewDate(new Date());

  return (
    <>
      <Chip
        size="small"
        onClick={e => setCalendarAnchor(e.currentTarget)}
        label={`${now.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' })} · ${now.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}`}
        sx={{ fontSize: 11, height: 24, bgcolor: P.action.hover, color: P.text.disabled, cursor: 'pointer', '&:hover': { bgcolor: P.action.hover } }}
      />

      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => { setCalendarAnchor(null); setCalViewDate(new Date()); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', overflow: 'hidden', minWidth: 280, userSelect: 'none' } } }}
      >
        <Box sx={{ p: 2 }}>
          {/* Month navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <IconButton size="small" onClick={calPrev} sx={{ width: 28, height: 28 }}>
              <Typography sx={{ fontSize: 14 }}>‹</Typography>
            </IconButton>
            <Typography
              onClick={calToday}
              sx={{ fontSize: 14, fontWeight: 600, cursor: 'pointer', '&:hover': { color: P.primary.main } }}
            >
              {calViewDate.toLocaleDateString('en', { month: 'long', year: 'numeric' })}
            </Typography>
            <IconButton size="small" onClick={calNext} sx={{ width: 28, height: 28 }}>
              <Typography sx={{ fontSize: 14 }}>›</Typography>
            </IconButton>
          </Box>
          {/* Weekday headers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25, mb: 0.5 }}>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <Typography key={d} sx={{ fontSize: 10, fontWeight: 600, textAlign: 'center', color: P.text.disabled }}>
                {d}
              </Typography>
            ))}
          </Box>
          {/* Days grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25 }}>
            {calDays.map((d, i) => {
              const isToday = isCurrentMonth && d === todayDate;
              return (
                <Box key={i} sx={{
                  width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%', fontSize: 12, mx: 'auto',
                  fontWeight: isToday ? 600 : 400,
                  bgcolor: isToday ? P.primary.main : 'transparent',
                  color: isToday ? P.primary.contrastText : (d ? undefined : 'transparent'),
                }}>
                  {d ?? ''}
                </Box>
              );
            })}
          </Box>
          {/* Current time */}
          <Typography sx={{ fontSize: 11, color: P.text.disabled, textAlign: 'center', mt: 1.5 }}>
            {now.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
